import React, { useEffect, useRef, useState } from "react";
import LayersIcon from "@mui/icons-material/Layers";
import { apiV2 } from "@/api/axiosConfig";
import { useDepartmentApi } from "@/api/platform/useDepartments";
import { useQuery } from "react-query";
import { ConnectionState } from "../../../../utils/WebSocketManager";
import { useMapSettings } from "../../context/useMapSettings";
import type { IncidentFeed, UserLocationType, TripType, SiteType } from "../../context/MapSettingsContext";
import type { UserLocation } from "../../../map/layers/useUserLocationsLayer";
import { TrackingMenu } from "./TrackingMenu";
import { MessageMenu } from "./MessageMenu";
import { useAuth } from "../../../../context/user";
import { fetchSearchClient } from "../../../../lib/clients/useClientSearch";
import type { Client, Department } from "../../../../types/userManagement";
import type { Overlay } from "../../../../types/overlay";

type AreaSelectMode = "polygon" | "circle" | null;

type Props = {
    wsState: ConnectionState;
    incidentCount: number;
    userCount: number;
    usersLoading: boolean;
    tripCount: number;
    facilityCount: number;
    legacyFacilityCount: number;
    geofenceCount: number;
    safetyZoneCount: number;
    deviceCount: number;
    onFitOverlay?: (id: string) => void;
    areaSelectMode: AreaSelectMode;
    areaSelectOpen: boolean;
    onAreaSelectToggle: () => void;
    onAreaSelectMode: (mode: AreaSelectMode) => void;
    onUserSearchChange: (q: string) => void;
    userList: UserLocation[];
};

function wsStatusColor(state: ConnectionState): string {
    switch (state) {
        case ConnectionState.CONNECTED:    return "bg-green";
        case ConnectionState.CONNECTING:
        case ConnectionState.RECONNECTING: return "bg-yellow";
        default:                           return "bg-red";
    }
}

function wsStatusLabel(state: ConnectionState): string {
    switch (state) {
        case ConnectionState.CONNECTED:    return "Connected";
        case ConnectionState.CONNECTING:   return "Connecting";
        case ConnectionState.RECONNECTING: return "Reconnecting";
        default:                           return "Disconnected";
    }
}

const USER_LOCATION_TYPES: { value: UserLocationType; label: string }[] = [
    { value: "app",       label: "App Users" },
    { value: "trip",      label: "Travellers" },
    { value: "facility",  label: "Static Employees" },
    { value: "satellite", label: "Devices (User Assigned)" },
];

const TRIP_TYPES: { value: TripType; label: string }[] = [
    { value: "travel",        label: "Travel" },
    { value: "accommodation", label: "Accommodation" },
];



type Pane = "incidents" | "users" | "trips" | "sites" | "overlays" | "devices" | "admin";

export function MapControlsMenu({ wsState, incidentCount, userCount, usersLoading, tripCount, facilityCount, legacyFacilityCount, geofenceCount, safetyZoneCount, deviceCount, onFitOverlay, areaSelectMode, areaSelectOpen, onAreaSelectToggle, onAreaSelectMode, onUserSearchChange, userList }: Props) {
    const { isSuperAdmin, clientID: userClientID } = useAuth();
    const deptApi = useDepartmentApi();
    const { settings, setIncidentFeeds, setShowIncidentRadius, setClusterIncidents, setUserTypes, setClusterUsers, setAutoReloadUsers, setTripTypes, setTripBufferHours, setShowFacilities, setShowLegacyFacilities, setSiteTypes, setSelectedOverlayIDs, setClientID, setDepartmentIDs, setShowDevices } = useMapSettings();
    const { incidentFeeds: selectedFeeds, showIncidentRadius, clusterIncidents, userTypes, clusterUsers, autoReloadUsers, tripTypes, tripBufferHours, showFacilities, showLegacyFacilities, siteTypes, selectedOverlayIDs, clientID, departmentIDs, trackingUserID, showDevices } = settings;

    const [open, setOpen] = useState(false);
    const [trackingOpen, setTrackingOpen] = useState(false);
    const [messageOpen, setMessageOpen] = useState(false);
    const [pane, setPane] = useState<Pane>("users");
    const [allFeeds, setAllFeeds] = useState<IncidentFeed[]>([]);
    const [search, setSearch] = useState("");
    const containerRef = useRef<HTMLDivElement>(null);

    // User search — debounced 300ms before notifying parent
    const [userSearchInput, setUserSearchInput] = useState("");

    useEffect(() => {
        const t = setTimeout(() => onUserSearchChange(userSearchInput.trim()), 300);
        return () => clearTimeout(t);
    }, [userSearchInput, onUserSearchChange]);

    // Overlays pane
    const [overlaySearch, setOverlaySearch] = useState("");
    const { data: allOverlays = [] as Overlay[] } = useQuery<Overlay[]>(
        ["ops-overlays"],
        async () => {
            const res = await apiV2.get("ops/overlays");
            return res.data ?? [];
        },
        { staleTime: 5 * 60_000 },
    );
    const visibleOverlays = allOverlays
        .filter((o) => o.name.toLowerCase().includes(overlaySearch.toLowerCase()))
        .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: "base" }));

    function toggleOverlay(id: string) {
        const isAdding = !selectedOverlayIDs.includes(id);
        setSelectedOverlayIDs(
            isAdding
                ? [...selectedOverlayIDs, id]
                : selectedOverlayIDs.filter((x) => x !== id)
        );
        if (isAdding) onFitOverlay?.(id);
    }

    // Admin pane — client search
    const [clientSearch, setClientSearch] = useState("");
    const [clientResults, setClientResults] = useState<Client[]>([]);
    const [selectedClient, setSelectedClient] = useState<Client | null>(null);
    const clientSearchRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    // Users pane — department search
    const [departmentSearch, setDepartmentSearch] = useState("");
    const [departmentResults, setDepartmentResults] = useState<Department[]>([]);
    const [departmentTotal, setDepartmentTotal] = useState(0);
    const [departmentPage, setDepartmentPage] = useState(1);
    const [selectedDepartments, setSelectedDepartments] = useState<Department[]>([]);
    const departmentSearchRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const selectedIDs = selectedFeeds.map((f) => f.id);

    const visibleFeeds = allFeeds
        .filter((f) => f.name.toLowerCase().includes(search.toLowerCase()))
        .slice(0, 5);

    useEffect(() => {
        const params: Record<string, string | number> = { page: 1, limit: 50 };
        const effectiveClientID = clientID ?? (isSuperAdmin ? userClientID : undefined);
        if (effectiveClientID) params.clientID = effectiveClientID;
        apiV2.get("incident-feeds", { params })
            .then((r) => {
                const feeds: (IncidentFeed & { isDefault?: boolean })[] = r.data.items ?? [];
                setAllFeeds(feeds);
                // Clear any selected feeds that no longer belong to this client
                const newIDs = new Set<string>(feeds.map((f) => f.id));
                const stillValid = selectedFeeds.filter((f) => newIDs.has(f.id));
                if (stillValid.length > 0) {
                    setIncidentFeeds(stillValid);
                } else {
                    // No saved selection — default to the default feed if one exists
                    const defaultFeed = feeds.find((f) => f.isDefault);
                    setIncidentFeeds(defaultFeed ? [{ id: defaultFeed.id, name: defaultFeed.name }] : []);
                }
            })
            .catch(() => {});
    }, [clientID, isSuperAdmin, userClientID]); // eslint-disable-line

    useEffect(() => {
        if (!open && !areaSelectOpen) return;
        const handler = (e: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
                if (open) setOpen(false);
                if (areaSelectOpen) onAreaSelectToggle();
            }
        };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, [open, areaSelectOpen]); // eslint-disable-line

    useEffect(() => {
        if (pane !== "admin") return;
        if (clientSearchRef.current) clearTimeout(clientSearchRef.current);
        clientSearchRef.current = setTimeout(async () => {
            const result = await fetchSearchClient(clientSearch, 10);
            setClientResults(result?.items ?? []);
        }, 250);
        return () => { if (clientSearchRef.current) clearTimeout(clientSearchRef.current); };
    }, [clientSearch, pane]);

    useEffect(() => {
        setDepartmentPage(1);
    }, [departmentSearch, clientID, userClientID]);

    useEffect(() => {
        if (pane !== "users") return;
        if (departmentSearchRef.current) clearTimeout(departmentSearchRef.current);
        departmentSearchRef.current = setTimeout(async () => {
            const effectiveClientID = clientID ?? userClientID;
            if (!effectiveClientID) return;
            const [res] = await deptApi.list({ clientID: effectiveClientID, search: departmentSearch, limit: 5, page: departmentPage });
            setDepartmentResults(res?.items ?? []);
            setDepartmentTotal(res?.total ?? 0);
        }, 250);
        return () => { if (departmentSearchRef.current) clearTimeout(departmentSearchRef.current); };
    }, [departmentSearch, pane, clientID, userClientID, departmentPage]);

    function selectClient(client: Client | null) {
        setSelectedClient(client);
        setClientID(client?.id ?? undefined);
        clearDepartments();
        setDepartmentSearch("");
        setDepartmentPage(1);
    }

    function toggleDepartment(department: Department) {
        const isSelected = selectedDepartments.some((d) => d.id === department.id);
        const next = isSelected
            ? selectedDepartments.filter((d) => d.id !== department.id)
            : [...selectedDepartments, department];
        setSelectedDepartments(next);
        setDepartmentIDs(next.map((d) => d.id));
    }

    function clearDepartments() {
        setSelectedDepartments([]);
        setDepartmentIDs([]);
    }

    function toggleFeed(feed: IncidentFeed) {
        const isSelected = selectedIDs.includes(feed.id);
        setIncidentFeeds(isSelected
            ? selectedFeeds.filter((f) => f.id !== feed.id)
            : [...selectedFeeds, feed]
        );
    }

    function toggleUserType(type: UserLocationType) {
        setUserTypes(userTypes.includes(type)
            ? userTypes.filter((t) => t !== type)
            : [...userTypes, type]
        );
    }

    function toggleTripType(type: TripType) {
        setTripTypes(tripTypes.includes(type)
            ? tripTypes.filter((t) => t !== type)
            : [...tripTypes, type]
        );
    }

    function toggleSiteType(type: SiteType) {
        setSiteTypes(siteTypes.includes(type)
            ? siteTypes.filter((t) => t !== type)
            : [...siteTypes, type]
        );
    }

    return (
        <div ref={containerRef} className="absolute top-4 left-4 z-50 flex items-start gap-2">
            <div className="relative">
            <button
                onClick={() => { setOpen((prev) => !prev); setTrackingOpen(false); if (areaSelectOpen) onAreaSelectToggle(); }}
                title={trackingUserID ? "Disabled while tracking a user" : "Map Layers"}
                disabled={!!trackingUserID}
                className={`w-12 h-12 flex items-center justify-center bg-white border-none rounded-lg shadow-[0_2px_8px_rgba(0,0,0,0.18)] transition-colors ${trackingUserID ? "opacity-50 cursor-not-allowed" : "cursor-pointer hover:bg-ice"}`}
            >
                <LayersIcon style={{ color: "#3b3b3b", fontSize: 24 }} />
            </button>

            {open && !trackingUserID && (
                <div className="absolute top-14 left-0 w-max min-w-[320px] bg-white rounded-xl shadow-[0_8px_24px_rgba(0,0,0,0.18)] overflow-hidden">
                    {/* Pane tabs */}
                    <div className="flex border-b border-light-light-grey">
                        {(["users", "devices", "incidents", "trips", "sites", "overlays", ...(isSuperAdmin ? ["admin"] : [])] as Pane[]).map((p) => (
                            <button
                                key={p}
                                onClick={() => setPane(p)}
                                className={`flex-1 py-2.5 px-4 text-[12px] font-semibold border-none cursor-pointer transition-colors border-b-2 whitespace-nowrap ${
                                    pane === p
                                        ? "bg-white text-blue border-b-blue"
                                        : "bg-ice text-grey border-b-transparent"
                                }`}
                            >
                                {p === "incidents" ? "Incidents" : p === "users" ? "Users" : p === "trips" ? "Trips" : p === "sites" ? "Sites" : p === "overlays" ? "Overlays" : p === "devices" ? "Devices" : "Admin"}
                            </button>
                        ))}
                    </div>

                    <div className="p-4 flex flex-col">
                        {pane === "incidents" ? (
                            <>
                                {allFeeds.length !== 1 && (
                                    <>
                                        <p className="text-[11px] font-semibold uppercase tracking-[0.06em] text-grey mb-2 mt-0">Incident Feeds</p>

                                        <input
                                            type="text"
                                            value={search}
                                            onChange={(e) => setSearch(e.target.value)}
                                            placeholder="Search feeds..."
                                            className="w-full text-[13px] px-2 py-1.5 border border-light-light-grey rounded-md outline-none mb-2 text-dark-dark-grey box-border"
                                        />

                                        {visibleFeeds.length === 0 ? (
                                            <p className="text-[13px] text-light-grey mb-1 mt-0">No feeds found</p>
                                        ) : (
                                            visibleFeeds.map((feed) => (
                                                <label key={feed.id} className="flex items-center justify-between py-1.5 cursor-pointer">
                                                    <span className="text-[13px] text-dark-dark-grey">{feed.name}</span>
                                                    <input
                                                        type="checkbox"
                                                        checked={selectedIDs.includes(feed.id)}
                                                        onChange={() => toggleFeed(feed)}
                                                        className="map-checkbox"
                                                    />
                                                </label>
                                            ))
                                        )}

                                        <hr className="border-none border-t border-light-light-grey my-3" />
                                    </>
                                )}

                                <p className="text-[11px] font-semibold uppercase tracking-[0.06em] text-grey mb-2 mt-0">Display</p>

                                {allFeeds.length === 1 && (
                                    <label className="flex items-center justify-between py-1.5 cursor-pointer">
                                        <span className="text-[13px] text-dark-dark-grey">Show Incidents</span>
                                        <input
                                            type="checkbox"
                                            checked={selectedIDs.includes(allFeeds[0].id)}
                                            onChange={() => toggleFeed(allFeeds[0])}
                                            className="map-checkbox"
                                        />
                                    </label>
                                )}

                                <label className="flex items-center justify-between py-1.5 cursor-pointer">
                                    <span className="text-[13px] text-dark-dark-grey">Show Radius</span>
                                    <input
                                        type="checkbox"
                                        checked={showIncidentRadius}
                                        onChange={(e) => setShowIncidentRadius(e.target.checked)}
                                        className="map-checkbox"
                                    />
                                </label>

                                <label className="flex items-center justify-between py-1.5 cursor-pointer">
                                    <span className="text-[13px] text-dark-dark-grey">Cluster Incidents</span>
                                    <input
                                        type="checkbox"
                                        checked={clusterIncidents}
                                        onChange={(e) => setClusterIncidents(e.target.checked)}
                                        className="map-checkbox"
                                    />
                                </label>

                                <hr className="border-none border-t border-light-light-grey my-3" />

                                <div className="flex items-center justify-between">
                                    {selectedIDs.length > 0
                                        ? <button onClick={() => setIncidentFeeds([])} className="text-[12px] text-light-grey bg-transparent border-none cursor-pointer p-0 hover:text-red">Hide all</button>
                                        : <span className="text-[12px] text-grey">{wsStatusLabel(wsState)}</span>}
                                    <div className="flex items-center gap-1.5">
                                        <span className={`w-2 h-2 rounded-full flex-shrink-0 ${wsStatusColor(wsState)}`} />
                                        <span className="text-[12px] font-semibold text-dark-dark-grey">
                                            {selectedIDs.length > 0 ? `${incidentCount} incidents` : "Select a feed"}
                                        </span>
                                    </div>
                                </div>
                            </>
                        ) : pane === "users" ? (
                            <>
                                <p className="text-[11px] font-semibold uppercase tracking-[0.06em] text-grey mb-2 mt-0">User Sources</p>

                                {USER_LOCATION_TYPES.map(({ value, label }) => (
                                    <label key={value} className="flex items-center justify-between py-1.5 cursor-pointer">
                                        <span className="text-[13px] text-dark-dark-grey">{label}</span>
                                        <input
                                            type="checkbox"
                                            checked={userTypes.includes(value)}
                                            onChange={() => toggleUserType(value)}
                                            className="map-checkbox"
                                        />
                                    </label>
                                ))}

                                <hr className="border-none border-t border-light-light-grey my-3" />

                                {(!isSuperAdmin || clientID) ? (
                                <>
                                <p className="text-[11px] font-semibold uppercase tracking-[0.06em] text-grey mb-2 mt-0">Department</p>


                                {selectedDepartments.length > 0 && (
                                    <div className="mb-1.5 flex flex-col gap-0.5">
                                        {selectedDepartments.map((d) => (
                                            <div key={d.id} className="flex items-center justify-between py-1">
                                                <span className="text-[13px] font-semibold text-dark-dark-grey truncate flex-1 mr-2">{d.name}</span>
                                                <button
                                                    onClick={() => toggleDepartment(d)}
                                                    className="text-[12px] text-light-grey bg-transparent border-none cursor-pointer p-0 flex-shrink-0 hover:text-red"
                                                >
                                                    Clear
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                )}

                                <input
                                    type="text"
                                    value={departmentSearch}
                                    onChange={(e) => setDepartmentSearch(e.target.value)}
                                    placeholder="Search departments..."
                                    className="w-full text-[13px] px-2 py-1.5 border border-light-light-grey rounded-md outline-none mb-2 text-dark-dark-grey box-border"
                                />

                                {departmentResults.filter((d) => !selectedDepartments.some((s) => s.id === d.id)).map((dept) => (
                                    <button
                                        key={dept.id}
                                        onClick={() => toggleDepartment(dept)}
                                        className="block w-full text-left px-2 py-1.5 rounded-md text-[13px] bg-transparent border-none cursor-pointer mb-0.5 text-dark-dark-grey hover:bg-ice"
                                    >
                                        {dept.name}
                                    </button>
                                ))}

                                {departmentTotal > 5 && (
                                    <div className="flex items-center justify-between mt-1">
                                        <button
                                            onClick={() => setDepartmentPage((p) => Math.max(1, p - 1))}
                                            disabled={departmentPage === 1}
                                            className="text-[12px] text-light-grey bg-transparent border-none cursor-pointer p-0 disabled:opacity-30 disabled:cursor-not-allowed hover:text-dark-dark-grey"
                                        >
                                            &larr; Prev
                                        </button>
                                        <span className="text-[11px] text-light-grey">{departmentPage} / {Math.ceil(departmentTotal / 5)}</span>
                                        <button
                                            onClick={() => setDepartmentPage((p) => p + 1)}
                                            disabled={departmentPage >= Math.ceil(departmentTotal / 5)}
                                            className="text-[12px] text-light-grey bg-transparent border-none cursor-pointer p-0 disabled:opacity-30 disabled:cursor-not-allowed hover:text-dark-dark-grey"
                                        >
                                            Next &rarr;
                                        </button>
                                    </div>
                                )}

                                <hr className="border-none border-t border-light-light-grey my-3" />
                                </>) : isSuperAdmin ? (
                                    <>
                                        <p className="text-[11px] font-semibold uppercase tracking-[0.06em] text-grey mb-2 mt-0">Department</p>
                                        <p className="text-[12px] text-light-grey mt-0 mb-3">Select a client in the Admin tab to filter by department.</p>
                                        <hr className="border-none border-t border-light-light-grey my-3" />
                                    </>
                                ) : null}

                                {userTypes.length > 0 && (
                                    <>
                                        <p className="text-[11px] font-semibold uppercase tracking-[0.06em] text-grey mb-2 mt-0">Search</p>
                                        <input
                                            type="text"
                                            placeholder="Filter users…"
                                            value={userSearchInput}
                                            onChange={(e) => setUserSearchInput(e.target.value)}
                                            autoComplete="new-password"
                                            disabled={usersLoading}
                                            className="w-full text-[13px] px-2 py-1.5 border border-light-light-grey rounded-md outline-none mb-3 text-dark-dark-grey box-border disabled:opacity-40 disabled:cursor-not-allowed"
                                        />
                                    </>
                                )}

                                <hr className="border-none border-t border-light-light-grey my-3" />

                                <p className="text-[11px] font-semibold uppercase tracking-[0.06em] text-grey mb-2 mt-0">Display</p>

                                <label className="flex items-center justify-between py-1.5 cursor-pointer">
                                    <span className="text-[13px] text-dark-dark-grey">Cluster Users</span>
                                    <input
                                        type="checkbox"
                                        checked={clusterUsers}
                                        onChange={(e) => setClusterUsers(e.target.checked)}
                                        className="map-checkbox"
                                    />
                                </label>

                                <label className="flex items-center justify-between py-1.5 cursor-pointer">
                                    <span className="text-[13px] text-dark-dark-grey">Auto Reload</span>
                                    <input
                                        type="checkbox"
                                        checked={autoReloadUsers}
                                        onChange={(e) => setAutoReloadUsers(e.target.checked)}
                                        className="map-checkbox"
                                    />
                                </label>

                                <hr className="border-none border-t border-light-light-grey my-3" />

                                <div className="flex items-center justify-between">
                                    {(userTypes.length > 0 || selectedDepartments.length > 0)
                                        ? <button onClick={() => { setUserTypes([]); clearDepartments(); }} className="text-[12px] text-light-grey bg-transparent border-none cursor-pointer p-0 hover:text-red">Hide all</button>
                                        : <span />}
                                    <span className="text-[12px] font-semibold text-dark-dark-grey">
                                        {usersLoading ? "Loading…" : userCount > 0 ? `${userCount} user${userCount !== 1 ? "s" : ""}${userSearchInput.trim() ? " (filtered)" : ""}` : userTypes.length > 0 ? "No users found" : "No sources selected"}
                                    </span>
                                </div>
                            </>
                        ) : pane === "devices" ? (
                            <>
                                <p className="text-[11px] font-semibold uppercase tracking-[0.06em] text-grey mb-2 mt-0">Device Sources</p>

                                <label className="flex items-center justify-between py-1.5 cursor-pointer">
                                    <span className="text-[13px] text-dark-dark-grey">Satellite</span>
                                    <input
                                        type="checkbox"
                                        checked={showDevices}
                                        onChange={(e) => setShowDevices(e.target.checked)}
                                        className="map-checkbox"
                                    />
                                </label>

                                <hr className="border-none border-t border-light-light-grey my-3" />

                                <div className="flex items-center justify-between">
                                    {showDevices
                                        ? <button onClick={() => setShowDevices(false)} className="text-[12px] text-light-grey bg-transparent border-none cursor-pointer p-0 hover:text-red">Hide all</button>
                                        : <span />}
                                    <span className="text-[12px] font-semibold text-dark-dark-grey">
                                        {showDevices ? (deviceCount > 0 ? `${deviceCount} device${deviceCount !== 1 ? "s" : ""}` : "No devices found") : "No sources selected"}
                                    </span>
                                </div>
                            </>
                        ) : pane === "sites" ? (
                            <>
                                <p className="text-[11px] font-semibold uppercase tracking-[0.06em] text-grey mb-2 mt-0">Site Types</p>

                                <label className="flex items-center justify-between py-1.5 cursor-pointer">
                                    <span className="text-[13px] text-dark-dark-grey">Facilities</span>
                                    <input
                                        type="checkbox"
                                        checked={showFacilities}
                                        onChange={(e) => setShowFacilities(e.target.checked)}
                                        className="map-checkbox"
                                    />
                                </label>
                                <label className="flex items-center justify-between py-1.5 cursor-pointer">
                                    <span className="text-[13px] text-dark-dark-grey">Facilities (Legacy)</span>
                                    <input
                                        type="checkbox"
                                        checked={showLegacyFacilities}
                                        onChange={(e) => setShowLegacyFacilities(e.target.checked)}
                                        className="map-checkbox"
                                    />
                                </label>
                                <label className="flex items-center justify-between py-1.5 cursor-pointer">
                                    <span className="text-[13px] text-dark-dark-grey">Geofences</span>
                                    <input
                                        type="checkbox"
                                        checked={siteTypes.includes("geofence")}
                                        onChange={() => toggleSiteType("geofence")}
                                        className="map-checkbox"
                                    />
                                </label>
                                <label className="flex items-center justify-between py-1.5 cursor-pointer">
                                    <span className="text-[13px] text-dark-dark-grey">Safety Zones</span>
                                    <input
                                        type="checkbox"
                                        checked={siteTypes.includes("safety-zone")}
                                        onChange={() => toggleSiteType("safety-zone")}
                                        className="map-checkbox"
                                    />
                                </label>

                                <hr className="border-none border-t border-light-light-grey my-3" />

                                <div className="flex items-center justify-between">
                                    {(showFacilities || showLegacyFacilities || siteTypes.length > 0)
                                        ? <button onClick={() => { setShowFacilities(false); setShowLegacyFacilities(false); setSiteTypes([]); }} className="text-[12px] text-light-grey bg-transparent border-none cursor-pointer p-0 hover:text-red">Hide all</button>
                                        : <span />}
                                    <span className="text-[12px] font-semibold text-dark-dark-grey">
                                        {(() => {
                                            const parts: string[] = [];
                                            if (showFacilities && facilityCount > 0) parts.push(`${facilityCount} facilit${facilityCount !== 1 ? "ies" : "y"}`);
                                            if (showLegacyFacilities && legacyFacilityCount > 0) parts.push(`${legacyFacilityCount} legacy facilit${legacyFacilityCount !== 1 ? "ies" : "y"}`);
                                            if (siteTypes.includes("geofence") && geofenceCount > 0) parts.push(`${geofenceCount} geofence${geofenceCount !== 1 ? "s" : ""}`);
                                            if (siteTypes.includes("safety-zone") && safetyZoneCount > 0) parts.push(`${safetyZoneCount} safety zone${safetyZoneCount !== 1 ? "s" : ""}`);
                                            return parts.length > 0 ? parts.join(", ") : "None visible";
                                        })()}
                                    </span>
                                </div>
                            </>
                        ) : pane === "trips" ? (
                            <>
                                <p className="text-[11px] font-semibold uppercase tracking-[0.06em] text-grey mb-2 mt-0">Trip Types</p>

                                {TRIP_TYPES.map(({ value, label }) => (
                                    <label key={value} className="flex items-center justify-between py-1.5 cursor-pointer">
                                        <span className="text-[13px] text-dark-dark-grey">{label}</span>
                                        <input
                                            type="checkbox"
                                            checked={tripTypes.includes(value)}
                                            onChange={() => toggleTripType(value)}
                                            className="map-checkbox"
                                        />
                                    </label>
                                ))}

                                <hr className="border-none border-t border-light-light-grey my-3" />

                                <p className="text-[11px] font-semibold uppercase tracking-[0.06em] text-grey mb-2 mt-0">Time Buffer</p>
                                <div className="flex items-center gap-2">
                                    <input
                                        type="range"
                                        min={0}
                                        max={24}
                                        step={1}
                                        value={tripBufferHours}
                                        onChange={(e) => setTripBufferHours(Number(e.target.value))}
                                        className="flex-1 accent-blue"
                                    />
                                    <span className="text-[12px] text-dark-dark-grey w-12 text-right flex-shrink-0">
                                        {tripBufferHours === 0 ? "Exact" : `±${tripBufferHours}h`}
                                    </span>
                                </div>
                                <p className="text-[11px] text-light-grey mt-1 mb-0">Includes trip itinerary items that ended recently or are starting soon</p>

                                <hr className="border-none border-t border-light-light-grey my-3" />

                                <div className="flex items-center justify-between">
                                    {tripTypes.length > 0
                                        ? <button onClick={() => setTripTypes([])} className="text-[12px] text-light-grey bg-transparent border-none cursor-pointer p-0 hover:text-red">Hide all</button>
                                        : <span />}
                                    <span className="text-[12px] font-semibold text-dark-dark-grey">
                                        {tripCount > 0 ? `${tripCount} trip${tripCount !== 1 ? "s" : ""}` : tripTypes.length > 0 ? "Loading…" : "No types selected"}
                                    </span>
                                </div>
                            </>
                        ) : pane === "overlays" ? (
                            <>
                                <p className="text-[11px] font-semibold uppercase tracking-[0.06em] text-grey mb-2 mt-0">Overlays</p>

                                <input
                                    type="text"
                                    value={overlaySearch}
                                    onChange={(e) => setOverlaySearch(e.target.value)}
                                    placeholder="Search overlays..."
                                    className="w-full text-[13px] px-2 py-1.5 border border-light-light-grey rounded-md outline-none mb-2 text-dark-dark-grey box-border"
                                />

                                {visibleOverlays.length === 0 ? (
                                    <p className="text-[13px] text-light-grey mb-1 mt-0">No overlays found</p>
                                ) : (
                                    visibleOverlays.map((overlay) => (
                                        <label key={overlay.id} className="flex items-center justify-between py-1.5 cursor-pointer">
                                            <span className="text-[13px] text-dark-dark-grey">{overlay.name}</span>
                                            <input
                                                type="checkbox"
                                                checked={selectedOverlayIDs.includes(overlay.id)}
                                                onChange={() => toggleOverlay(overlay.id)}
                                                className="map-checkbox"
                                            />
                                        </label>
                                    ))
                                )}

                                <hr className="border-none border-t border-light-light-grey my-3" />

                                <div className="flex items-center justify-between">
                                    {selectedOverlayIDs.length > 0
                                        ? <button onClick={() => setSelectedOverlayIDs([])} className="text-[12px] text-light-grey bg-transparent border-none cursor-pointer p-0 hover:text-red">Hide all</button>
                                        : <span />}
                                    <span className="text-[12px] font-semibold text-dark-dark-grey">
                                        {selectedOverlayIDs.length > 0 ? `${selectedOverlayIDs.length} active` : "None selected"}
                                    </span>
                                </div>
                            </>
                        ) : (
                            <>
                                <p className="text-[11px] font-semibold uppercase tracking-[0.06em] text-grey mb-2 mt-0">Client</p>

                                {selectedClient ? (
                                    <div className="flex items-center justify-between py-1.5 mb-1">
                                        <span className="text-[13px] font-semibold text-dark-dark-grey truncate flex-1 mr-2">{selectedClient.name}</span>
                                        <button
                                            onClick={() => selectClient(null)}
                                            className="text-[12px] text-light-grey bg-transparent border-none cursor-pointer p-0 flex-shrink-0 hover:text-red"
                                        >
                                            Clear
                                        </button>
                                    </div>
                                ) : (
                                    <p className="text-[13px] text-light-grey mb-2 mt-0">No client selected - showing all</p>
                                )}

                                <input
                                    type="text"
                                    value={clientSearch}
                                    onChange={(e) => setClientSearch(e.target.value)}
                                    placeholder="Search clients..."
                                    className="w-full text-[13px] px-2 py-1.5 border border-light-light-grey rounded-md outline-none mb-2 text-dark-dark-grey box-border"
                                />

                                {clientResults.length === 0 ? (
                                    <p className="text-[13px] text-light-grey mb-1 mt-0">No clients found</p>
                                ) : (
                                    clientResults.slice(0, 5).map((client) => (
                                        <button
                                            key={client.id}
                                            onClick={() => { selectClient(client); setClientSearch(""); setClientResults([]); }}
                                            className={`block w-full text-left px-2 py-1.5 rounded-md text-[13px] bg-transparent border-none cursor-pointer mb-0.5 ${
                                                clientID === client.id ? "bg-ice text-blue font-semibold" : "text-dark-dark-grey hover:bg-ice"
                                            }`}
                                        >
                                            {client.name}
                                        </button>
                                    ))
                                )}
                            </>
                        )}
                    </div>
                </div>
            )}
            </div>

            {/* Area select button — only shown when users are on the map */}
            {userCount > 0 && <div className="relative">
                <button
                    onClick={() => { onAreaSelectToggle(); setOpen(false); setTrackingOpen(false); }}
                    title={trackingUserID ? "Disabled while tracking a user" : "Select users by area"}
                    disabled={!!trackingUserID}
                    className={`flex items-center gap-1.5 px-3 h-12 rounded-lg shadow-[0_2px_8px_rgba(0,0,0,0.18)] text-sm font-medium transition-colors border-none ${
                        trackingUserID
                            ? "bg-white text-light-grey cursor-not-allowed opacity-50"
                            : areaSelectMode
                                ? "bg-blue text-white hover:brightness-110 cursor-pointer"
                                : "bg-white text-grey hover:bg-ice cursor-pointer"
                    }`}
                >
                    <span className="material-icons text-[18px] leading-none">highlight_alt</span>
                    {areaSelectMode ? (
                        <span>Drawing{areaSelectMode === "polygon" ? " polygon" : " circle"}…</span>
                    ) : (
                        <span>Select users</span>
                    )}
                    <span className="material-icons text-[14px] leading-none opacity-60">arrow_drop_down</span>
                </button>

                {areaSelectOpen && !trackingUserID && (
                    <div className="absolute left-0 top-full mt-1 bg-white rounded-lg shadow-[0_4px_16px_rgba(0,0,0,0.15)] py-1 min-w-[160px] z-50">
                        <button
                            onClick={() => onAreaSelectMode("polygon")}
                            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-dark-dark-grey hover:bg-light-light-grey text-left bg-transparent border-none cursor-pointer"
                        >
                            <span className="material-icons text-[16px]">pentagon</span>
                            Draw polygon
                        </button>
                        <button
                            onClick={() => onAreaSelectMode("circle")}
                            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-dark-dark-grey hover:bg-light-light-grey text-left bg-transparent border-none cursor-pointer"
                        >
                            <span className="material-icons text-[16px]">circle</span>
                            Draw circle
                        </button>
                    </div>
                )}
            </div>}

            <TrackingMenu
                userList={userList}
                open={trackingOpen}
                onOpenChange={(v) => { setTrackingOpen(v); if (v) { setOpen(false); setMessageOpen(false); if (areaSelectOpen) onAreaSelectToggle(); } }}
            />

            <MessageMenu
                open={messageOpen}
                onOpenChange={(v) => { setMessageOpen(v); if (v) { setOpen(false); setTrackingOpen(false); if (areaSelectOpen) onAreaSelectToggle(); } }}
            />
        </div>
    );
}
